import { useMutation, useQueryClient } from '@tanstack/react-query';

import { BankLinkEventType, convertToTaxonomyCase, EVENTS, useIdentify, useTrack } from '@arrived/analytics';
import { postPlaidVerifyExternalAccount } from '@arrived/api_v2';
import { trackMarketingConversionEvent } from '@arrived/marketing';
import { Account, AccountId, ExternalAccount, MARKETING_CONVERSION_EVENTS } from '@arrived/models';

import { UseMutationOptions } from '../../queries.utils';
import { accountsPrimaryKeyFn } from '../../users';
import { accountsKeyFn } from '../accounts.keys';

export function usePostVerifyExternalAccountMutation(options?: UseMutationOptions<ExternalAccount, AccountId>) {
  const queryClient = useQueryClient();
  const track = useTrack();
  const identify = useIdentify();

  return useMutation((accountId) => postPlaidVerifyExternalAccount(accountId), {
    ...options,
    onSuccess: (data, variables, context) => {
      track(EVENTS.BANK_LINK, {
        update_type: BankLinkEventType.VERIFY,
        verification_status: convertToTaxonomyCase(data.verified),
        connection_type: convertToTaxonomyCase(data.linkType),
      });

      identify({
        has_had_bank_account_linked: true,
      });

      trackMarketingConversionEvent({
        type: MARKETING_CONVERSION_EVENTS.LINKED_BANK,
      });

      const primaryAccount = queryClient.getQueryData<Account>(accountsPrimaryKeyFn());

      if (primaryAccount) {
        queryClient.setQueryData(accountsPrimaryKeyFn(), { ...primaryAccount, externalAccount: data });
      }

      const account = queryClient.getQueryData<Account>(accountsKeyFn(variables));

      if (account) {
        queryClient.setQueryData(accountsKeyFn(variables), { ...account, externalAccount: data });
      }

      queryClient.invalidateQueries(accountsPrimaryKeyFn());

      options?.onSuccess?.(data, variables, context);
    },
  });
}
